/*
 * Copyright (c) Funtip GbR 2018.
 * Simon Oldeboershuis, Perry Prust
 */

import { all, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { fetchWrapperFuncNoLoading } from '../../../utils';
import { getPlaylistAdminApi } from '../../System/systemConfig';
import {
    searchSong,
    SEARCH_SONG,
    setSongList,
    setSongSearch,
    setSongSearchCount,
    setSongSearchCurrentPos,
    songListSetLoading,
    SONG_LIST_BACKWARD,
    SONG_LIST_FAST_BACKWARD,
    SONG_LIST_FAST_FORWARD,
    SONG_LIST_FORWARD,
    SONG_LIST_SEARCH
} from './actions';
import { songListSearchString, songSearchCount, songSearchCurrentPos, songSearchPageSize } from './selectors';

const playlistApi = getPlaylistAdminApi();

function* searchSongSearchGenerator() {
    const searchString: string = yield select(songListSearchString);
    const pageSize: number = yield select(songSearchPageSize);
    const currentPos: number = yield select(songSearchCurrentPos);

    yield put(songListSetLoading(true));

    try {
        yield* fetchWrapperFuncNoLoading(() => playlistApi.searchSongs(currentPos, pageSize, searchString), [
            r => setSongList(r)
        ]);

        yield* fetchWrapperFuncNoLoading(() => playlistApi.countSongs(searchString), [r => setSongSearchCount(r)]);
    } finally {
        yield put(songListSetLoading(false));
    }
}

function* search(action: Action<string>) {
    yield put(setSongSearch(action.payload));
    yield put(searchSong());
}

function* songListForwardGenerator() {
    const pageSize: number = yield select(songSearchPageSize);
    const count: number = yield select(songSearchCount);
    const currentPos: number = yield select(songSearchCurrentPos);

    if (currentPos + pageSize <= count) {
        yield put(setSongSearchCurrentPos(currentPos + pageSize));
        yield put(searchSong());
    }
}

function* songListBackwardGenerator() {
    const pageSize: number = yield select(songSearchPageSize);
    const currentPos: number = yield select(songSearchCurrentPos);

    if (currentPos > 0) {
        const nextPos = currentPos > pageSize ? currentPos - pageSize : 0;
        yield put(setSongSearchCurrentPos(nextPos));
        yield put(searchSong());
    }
}
function* songListFastBackwardGenerator() {
    const currentPos: number = yield select(songSearchCurrentPos);
    if (currentPos > 0) {
        yield put(setSongSearchCurrentPos(0));
        yield put(searchSong());
    }
}
function* songListFastForwardGenerator() {
    const pageSize: number = yield select(songSearchPageSize);
    const count: number = yield select(songSearchCount);
    const currentPos: number = yield select(songSearchCurrentPos);

    const nextPos = count - pageSize > 0 ? count - pageSize : 0;
    if (currentPos !== nextPos) {
        yield put(setSongSearchCurrentPos(nextPos));
        yield put(searchSong());
    }
}
function* watchForSongListActions() {
    yield takeLatest(SONG_LIST_FORWARD, songListForwardGenerator);
    yield takeLatest(SONG_LIST_BACKWARD, songListBackwardGenerator);
    yield takeLatest(SONG_LIST_FAST_FORWARD, songListFastForwardGenerator);
    yield takeLatest(SONG_LIST_FAST_BACKWARD, songListFastBackwardGenerator);
    yield takeLatest(SEARCH_SONG, searchSongSearchGenerator);
    yield takeLatest(SONG_LIST_SEARCH, search);
}

export function* songSagas() {
    yield all([watchForSongListActions()]);
}
